import type { MentorMe, MentorProfileFormValues } from './types';

export type MentorProfileStepId =
  | 'basic'
  | 'experience'
  | 'skills'
  | 'languages'
  | 'availability';

export type MentorProfileStepProgress = {
  id: MentorProfileStepId;
  filled: number;
  total: number;
  complete: boolean;
};

export type MentorProfileStepsState = {
  steps: MentorProfileStepProgress[];
  percent: number;
  firstIncompleteId: MentorProfileStepId | null;
};

function filled(value: string | null | undefined): boolean {
  return Boolean(value?.trim());
}

function countFilled(checks: boolean[]): { filled: number; total: number } {
  return {
    filled: checks.filter(Boolean).length,
    total: checks.length,
  };
}

/**
 * Onboarding steps for mentor profile. Totals match the product wireframe;
 * fields not yet in the API still count toward `total` (always empty).
 */
export function computeMentorProfileSteps(
  values: MentorProfileFormValues,
  _mentor: MentorMe | null | undefined,
  options?: { hasAvatar?: boolean; hasStandardPrice?: boolean },
): MentorProfileStepsState {
  const hasAvatar = options?.hasAvatar ?? filled(values.avatarUrl);
  const hasStandardPrice = options?.hasStandardPrice ?? false;

  const basic = countFilled([
    filled(values.displayName),
    filled(values.headline),
    filled(values.description),
  ]);

  const experience = countFilled([
    filled(values.goals),
    filled(values.experienceYears) && Number(values.experienceYears) >= 0,
    false, // education — future
    false, // certifications — future
  ]);

  const skills = countFilled([
    values.categoryIds.length > 0,
    false, // skills — future
    false, // tags — future
  ]);

  const languages = countFilled([
    values.nativeLanguages.length > 0,
    false, // working languages — future
  ]);

  const availability = countFilled([
    hasStandardPrice,
    false, // schedule — future
  ]);

  const steps: MentorProfileStepProgress[] = [
    { id: 'basic', ...basic, complete: basic.filled === basic.total },
    {
      id: 'experience',
      ...experience,
      complete: experience.filled === experience.total,
    },
    { id: 'skills', ...skills, complete: skills.filled === skills.total },
    {
      id: 'languages',
      ...languages,
      complete: languages.filled === languages.total,
    },
    {
      id: 'availability',
      ...availability,
      complete: availability.filled === availability.total,
    },
  ];

  // Overall % also credits avatar + timezone + intro video (media/details).
  const extraChecks = [
    hasAvatar,
    filled(values.timezone),
    filled(values.introVideoUrl),
  ];
  const allChecks = [
    ...[basic, experience, skills, languages, availability].flatMap((s) =>
      Array.from({ length: s.total }, (_, i) => i < s.filled),
    ),
    ...extraChecks,
  ];
  const done = allChecks.filter(Boolean).length;
  const percent = Math.round((done / allChecks.length) * 100);

  const firstIncomplete =
    steps.find((step) => !step.complete)?.id ?? null;

  return {
    steps,
    percent: Math.min(100, percent),
    firstIncompleteId: firstIncomplete,
  };
}

export const MENTOR_PROFILE_STEP_ORDER: MentorProfileStepId[] = [
  'basic',
  'experience',
  'skills',
  'languages',
  'availability',
];
